'use client';

import ease from '@/constants/ease';
import useStore from '@/stores/useStore';
import cx from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import { FC, useState } from 'react';
import Drawer from './Drawer';
import IconChevronPricingDown from './IconChevronPricingDown';
import IconTooltip from './IconTooltip';
import Link from './Link';

export interface PlanProps {
  title: string;
  monthlyPrice?: string;
  monthlyPriceSubtitle?: string;
  yearlyPrice?: string;
  yearlyPriceSubtitle?: string;
  cta: {
    title: string;
    href: string;
    action: 'demo' | 'trial';
  };
}

export interface FeatureRowProps {
  activePlanIndex?: number;
  title: string;
  description?: string;
  tooltip?: string;
  columns: React.ReactNode[];
}

export interface FeatureTableProps {
  activePlanIndex?: number;
  isFirst?: boolean;
  isLast?: boolean;

  title: string;
  expanded?: boolean;
  headerColumns: React.ReactNode[];

  rows: FeatureRowProps[];
}

export interface PricingFeatureComparisonTableProps {
  plansTitle: string;
  plans: PlanProps[];

  featureRows: FeatureTableProps[];

  isYearlyPricingVisible?: boolean;
  setYearlyPricingVisible?: (boolean) => void;
}

const FeatureRow: FC<FeatureRowProps> = ({ title, description, tooltip, columns, activePlanIndex }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className="border-t border-solid border-violet-200 group lg:hover:bg-citron-100 lg:hover:text-citron-900 grid items-center grid-cols-4 lg:grid-cols-12 gap-x-gutter-mobile md:gap-x-gutter-desktop">
      <div className="col-span-3 lg:col-span-4 py-2 px-2 md:px-8">
        <div className="flex items-center gap-half">
          <p className="text-14/20 md:text-20/24 font-500 tracking-tight lg:group-hover:text-citron-900">{title}</p>
          {tooltip && (
            <div
              onClick={() => setShowTooltip(!showTooltip)}
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              className="relative group hidden lg:block"
            >
              <IconTooltip className="block" />
              <AnimatePresence>
                {showTooltip && (
                  <motion.div
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{
                      ease: ease.appear,
                      duration: 300 / 1000,
                    }}
                    className="absolute top-[-4px] left-4 py-1 px-[12px] bg-citron-500 w-[200px] z-1 rounded-4 text-12/16"
                  >
                    <Image
                      src="https://front.com/webassets/rebrand/arrow-pricing-tooltip.svg"
                      width={14}
                      height={27}
                      className="block absolute top-0 left-[-12px]"
                      alt=""
                    />
                    {tooltip}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
        <p className="text-13/18 md:text-17/24 text-violet-800 lg:group-hover:text-citron-900">{description}</p>
      </div>
      {columns.map((column, index) => (
        <div
          key={`feature-row-column-${index}`}
          className={cx('col-span-1 lg:col-span-2 py-3', {
            'hidden lg:block': index !== activePlanIndex,
          })}
        >
          <div className="flex justify-center items-center text-14/20 md:text-20/28">{column}</div>
        </div>
      ))}
    </div>
  );
};

const FeatureTable: FC<FeatureTableProps> = ({
  activePlanIndex,
  title,
  expanded,
  headerColumns = [],
  rows = [],
  isFirst,
  isLast,
}) => {
  const [isOpen, setOpen] = useState(false);

  return (
    <div
      className={cx('px-container-mobile md:px-container-desktop text-violet-900', {
        'mb-4': expanded,
      })}
    >
      <div className="mx-auto max-w-grid">
        <div className="-mx-2 md:-mx-8">
          <div
            className={cx('overflow-hidden', {
              'bg-violet-100 rounded-20 md:rounded-40': expanded,
              'border-b border-solid border-violet-200': !isFirst && !isLast,
            })}
          >
            <Drawer
              open={expanded || isOpen}
              renderTrigger={() => (
                <div className="relative grid items-center grid-cols-4 lg:grid-cols-12 gap-x-gutter-mobile md:gap-x-gutter-desktop">
                  <div className="col-span-3 lg:col-span-4 py-3 px-2 md:pl-8 md:pr-0">
                    <div className="flex items-center gap-1">
                      {!expanded && (
                        <motion.div
                          initial={false}
                          animate={{ rotate: isOpen ? 0 : -90 }}
                          transition={{
                            ease: ease.appear,
                            duration: 300 / 1000,
                          }}
                          className="md:ml-[-38px]"
                        >
                          <IconChevronPricingDown className="block" />
                        </motion.div>
                      )}
                      <p className="text-20/24 md:text-25/30 font-500 tracking-tight">{title}</p>
                    </div>
                  </div>
                  {headerColumns.map((column, index) => (
                    <div
                      key={`feature-table-header-column-${index}`}
                      className={cx('col-span-1 lg:col-span-2 py-3', {
                        'hidden lg:block': index !== activePlanIndex,
                      })}
                    >
                      <div className="flex justify-center items-center">{column}</div>
                    </div>
                  ))}
                  <button className="block absolute inset-0" onClick={() => setOpen(!isOpen)} />
                </div>
              )}
            >
              <div className="lg:pb-12">
                {rows.map((row, index) => (
                  <FeatureRow
                    key={`feature-row-${row.title?.replace(/\s/g, '-') ?? index}`}
                    {...row}
                    activePlanIndex={activePlanIndex}
                  />
                ))}
              </div>
            </Drawer>
          </div>
        </div>
      </div>
    </div>
  );
};

const PricingFeatureComparisonTable: FC<PricingFeatureComparisonTableProps> = ({
  plansTitle,
  plans = [],
  isYearlyPricingVisible,
  featureRows = [],
}) => {
  const [activePlanIndex, setActivePlanIndex] = useState(0);
  const activePlan = plans[activePlanIndex];
  const { setDemoModalOpen, setTrialModalOpen } = useStore();

  const modalActionHandler = (action) => {
    switch (action) {
      case 'trial':
        setTrialModalOpen(true);
        break;
      case 'demo':
        setDemoModalOpen(true);
        break;
      default:
        break;
    }
  };
  const onChangePlanIndex = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setActivePlanIndex(parseInt(event.target.value));
  };

  const mobilePlanSelect = (
    <div className="relative flex justify-between gap-2 lg:hidden">
      <div className="flex gap-2">
        <p className="text-25/30 font-500 tracking-tight">{activePlan.title}</p>
        <p>
          <span className="text-25/30 font-500 tracking-tight">
            {isYearlyPricingVisible ? activePlan.yearlyPrice : activePlan.monthlyPrice}
          </span>
          <span className="text-17/20">
            {isYearlyPricingVisible ? activePlan.yearlyPriceSubtitle : activePlan.monthlyPriceSubtitle}
          </span>
        </p>
      </div>
      <Image
        className="block"
        src="https://front.com/webassets/rebrand/icon-chevron-select-pricing-comparison.svg"
        alt="chevron"
        width={28}
        height={28}
      />
      <select value={activePlanIndex} onChange={onChangePlanIndex} className="block absolute inset-0 opacity-0">
        {plans.map((plan, index) => (
          <option key={`plan-select-${plan.title?.replace(/\s/g, '-') ?? index}`} value={index} selected={index === activePlanIndex}>
            {plan.title}
          </option>
        ))}
      </select>
    </div>
  );

  const desktopPlanColumns = plans.map((plan, index) => {
    return (
      <div key={`plan-column-${plan.title?.replace(/\s/g, '-') ?? index}`} className="col-span-2 hidden lg:block">
        <div className="flex flex-col items-center gap-2">
          <div className="flex flex-col justify-center items-center">
            <p className="text-25/30 font-500 tracking-tight">{plan.title}</p>
            <p className="text-14/20">
              {isYearlyPricingVisible ? plan.yearlyPrice : plan.monthlyPrice}
              {isYearlyPricingVisible ? plan.yearlyPriceSubtitle : plan.monthlyPriceSubtitle}
            </p>
          </div>
          {plan.cta.href.length > 0 ? (
            <Link
              href={plan.cta.href}
              className="min-w-[90px] block button button-size-small-medium button-primary-violet-lighter"
            >
              {plan.cta.title}
            </Link>
          ) : (
            <button
              onClick={() => {
                modalActionHandler(plan.cta.action);
              }}
              className="min-w-[90px] block button button-size-small-medium button-primary-violet-lighter"
            >
              {plan.cta.title}
            </button>
          )}
        </div>
      </div>
    );
  });

  const planSelectRibbon = (
    <div className="bg-violet-900 text-white sticky z-1 top-header-height-mobile md:top-header-height-desktop">
      <div className="px-container-mobile md:px-container-desktop py-1 lg:py-2">
        <div className="mx-auto max-w-grid">
          <div className="lg:-mx-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-gutter-mobile md:gap-x-gutter-desktop items-center">
              <div className="lg:col-span-4 lg:pl-8">
                <p className="text-17/24 lg:text-30/35 font-500 tracking-tight">{plansTitle}</p>
              </div>
              {desktopPlanColumns}
              {mobilePlanSelect}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const featureTables = featureRows.map((featureRow, index) => (
    <FeatureTable
      key={`feature-table-${featureRow.title?.replace(/\s/g, '-') ?? index}`}
      {...featureRow}
      activePlanIndex={activePlanIndex}
      isFirst={index === 0}
      isLast={index === featureRows.length - 1}
    />
  ));

  return (
    <div className="flex flex-col gap-4 md:gap-8 mb-8">
      {planSelectRibbon}
      <div>{featureTables}</div>
    </div>
  );
};

export default PricingFeatureComparisonTable;
